import React, { useState, useEffect, useRef } from 'react';
import { Formio } from '@formio/js';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useFormio } from './FormioProvider';
import { fetchFormioFormThunk } from '../../store/thunks/formioThunks';
import { useDirtyFieldTracker } from '../../hooks/useDirtyFieldTracker';
import LoadingBackdrop from '../common/custom-component/LoadingBackdrop';
import { logger } from 'helpers/logger';

// submissions: [{ formId, formPath, data }, ...]. Every entry's schema is
// fetched once, up front, in parallel - switching activeIndex afterwards
// (e.g. an outer stepper paging through several already-submitted forms)
// never triggers a new network fetch, just a re-mount of the Form.io widget
// with the already-cached schema. Single-form callers (Fill Form, Execute
// Task) just pass a one-item array.
const FormRenderer = ({ submissions, activeIndex = 0, onChange, onSubmit, onReady, onSchemasLoaded, readOnly = false }) => {
    const dispatch = useDispatch();
    const { isFormioReady } = useFormio();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [schemasReady, setSchemasReady] = useState(false);
    const schemasRef = useRef([]);
    const formRef = useRef(null);
    const formInstance = useRef(null);
    const { resetBaseline, trackChange, getDirtyFields } = useDirtyFieldTracker();

    const onChangeRef = useRef(onChange);
    const onSubmitRef = useRef(onSubmit);
    const onReadyRef = useRef(onReady);

    useEffect(() => {
        onChangeRef.current = onChange;
        onSubmitRef.current = onSubmit;
        onReadyRef.current = onReady;
    }, [onChange, onSubmit, onReady]);

    // Fetch every submission's schema once, in parallel, and cache them.
    useEffect(() => {
        // Nothing to load: say so instead of leaving the loading backdrop up forever.
        if (!submissions?.length) {
            setLoading(false);
            setError('No form to display');
            return;
        }
        // Wait until the user is logged in (the backend fetches the schema from Form.io with its
        // own service account) - this effect re-runs once isFormioReady turns true.
        if (!isFormioReady) {
            return;
        }

        let cancelled = false;
        schemasRef.current = new Array(submissions.length).fill(null);
        setSchemasReady(false);
        setLoading(true);
        setError(null);

        Promise.all(
            submissions.map((submission) =>
                dispatch(fetchFormioFormThunk({ formId: submission.formId, formPath: submission.formPath }))
            )
        ).then((results) => {
            if (cancelled) return;

            let failure = null;
            results.forEach((result, index) => {
                if (fetchFormioFormThunk.fulfilled.match(result)) {
                    schemasRef.current[index] = result.payload?.data || result.payload;
                } else {
                    failure = result.payload || 'Form not found';
                }
            });

            if (failure) {
                logger.error('❌ Error fetching form schemas:', failure);
                setError(failure);
            }
            setSchemasReady(true);
            setLoading(false);
            if (onSchemasLoaded) {
                onSchemasLoaded(schemasRef.current);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [submissions, dispatch, isFormioReady]);

    // (Re)mount the Form.io widget for whichever submission is active, once
    // its schema is cached - no fetch here, just instantiation, so stepping
    // between already-fetched submissions is instant.
    useEffect(() => {
        const schema = schemasRef.current[activeIndex];
        if (!schemasReady || !schema || !formRef.current) {
            return;
        }

        let destroyed = false;
        let instance = null;
        const initialData = submissions?.[activeIndex]?.data;

        resetBaseline(initialData);

        Formio.createForm(formRef.current, schema, {
            readOnly,
            noAlerts: true
        }).then((form) => {
            if (destroyed) {
                form.destroy();
                return;
            }
            instance = form;
            formInstance.current = form;

            if (initialData) {
                form.submission = { data: initialData };
                form.pristine = true;
            }

            if (onReadyRef.current) {
                onReadyRef.current();
            }

            if (!readOnly) {
                form.on('change', (changed) => {
                    if (!form.pristine) {
                        if (changed.changed && changed.changed.component) {
                            trackChange(changed.changed.component.key, changed.changed.value);
                        }

                        if (onChangeRef.current) {
                            onChangeRef.current({
                                ...changed,
                                changedFields: getDirtyFields(),
                                allData: form.data
                            });
                        }
                    }
                });

                form.on('submit', (submissionResult) => {
                    if (onSubmitRef.current) {
                        onSubmitRef.current({
                            changedFields: getDirtyFields(), // Only modified fields
                            fullData: submissionResult.data, // Complete form data
                            originalSubmission: submissionResult // Original submission object
                        });
                    }
                });
            }
        }).catch((err) => {
            logger.error('❌ Error creating form:', err);
        });

        return () => {
            destroyed = true;
            if (instance) {
                instance.destroy();
            }
            formInstance.current = null;
        };
    }, [activeIndex, schemasReady, readOnly, submissions, resetBaseline, trackChange, getDirtyFields]);

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">Error: {error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <LoadingBackdrop open={loading} message="Loading form, please wait..." variant="bars" color="greenGradient" />
            <div ref={formRef} className="formio-theme-scope" />
        </Box>
    );
};

export default React.memo(FormRenderer);
